import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Skeleton,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Message as MessageIcon,
  CheckCircle as ActiveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { botService, logService } from '../services/api';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const { data: botsData, isLoading: botsLoading } = useQuery(
    'bots',
    async () => {
      const response = await botService.list();
      return response.data;
    }
  );

  const { data: statsData } = useQuery(
    'dashboardStats',
    async () => {
      const response = await botService.getDashboardStats();
      return response.data;
    }
  );

  const activeBots = botsData?.bots?.filter((bot: any) => bot.is_active).length || 0;
  const totalBots = botsData?.bots?.length || 0;
  const todayMessages = statsData?.todayMessages || 0;

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4">{value}</Typography>
          </Box>
          <Box sx={{ color }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Bem-vindo à plataforma de gerenciamento de chatbots
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          {botsLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <StatCard
              title="Total de Bots"
              value={totalBots}
              icon={<BotIcon sx={{ fontSize: 40 }} />}
              color="#1976d2"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {botsLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <StatCard
              title="Bots Ativos"
              value={activeBots}
              icon={<ActiveIcon sx={{ fontSize: 40 }} />}
              color="#2e7d32"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Mensagens Hoje"
            value={todayMessages}
            icon={<MessageIcon sx={{ fontSize: 40 }} />}
            color="#ed6c02"
          />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Seus Bots</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/bots/new')}
              >
                Criar Novo Bot
              </Button>
            </Box>
            {botsLoading ? (
              <Box>
                <Skeleton variant="text" height={40} />
                <Skeleton variant="text" height={40} />
                <Skeleton variant="text" height={40} />
              </Box>
            ) : botsData?.bots?.length === 0 ? (
              <Box textAlign="center" py={4}>
                <BotIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  Você ainda não tem nenhum bot
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Crie seu primeiro bot para começar
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/bots/new')}
                >
                  Criar Bot
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {botsData.bots.slice(0, 3).map((bot: any) => (
                  <Grid item xs={12} md={4} key={bot.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 3 },
                      }}
                      onClick={() => navigate(`/bots/${bot.id}`)}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                          <BotIcon sx={{ mr: 1 }} />
                          <Typography variant="h6">{bot.name}</Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          Status: {bot.is_active ? 'Ativo' : 'Inativo'}
                        </Typography>
                        {bot.phone_number && (
                          <Typography variant="body2" color="textSecondary">
                            WhatsApp: {bot.phone_number}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            {botsData?.bots?.length > 3 && (
              <Box textAlign="center" mt={2}>
                <Button onClick={() => navigate('/bots')}>
                  Ver todos os bots
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;